/**
 * Your profile: picture, name, email, role, and your bio (public teammate facts
 * agents read). The Connections tab links your own tools for agents (Phase 4b):
 * CortexOS memory, any MCP server, then Gmail; only tools that read can be on.
 */
import { useEffect, useRef, useState } from "react";
import { IconMail } from "../components/icons";
import { Avatar, ErrorNote, Fold, Modal, ROLE_LABEL } from "../components/ui";
import { api, ApiError } from "../lib/api";
import { squareImage } from "../lib/image";
import { loadBootstrap, useStore } from "../lib/store";
import { applyTheme, readTheme, type Theme } from "../lib/theme";

const BIO_TEMPLATE = `Responsibilities
(what you own and decide)

Current goals
(what you are working toward now)

External accounts
(tools and accounts you use for work)

Work context
(companies, clients, products, anything teammates and agents should know)
`;

export function Profile() {
  const boot = useStore((s) => s.boot)!;
  const [tab, setTab] = useState<"profile" | "connections">(() => (location.hash === "#connections" ? "connections" : "profile"));
  useEffect(() => {
    history.replaceState(null, "", tab === "connections" ? "#connections" : location.pathname);
  }, [tab]);

  return (
    <main className="page profile-page">
      <header className="profile-head">
        <PictureButton />
        <div>
          <h1>{boot.me.name}</h1>
          <p className="muted-text">
            {ROLE_LABEL[boot.workspace.role]} of {boot.workspace.name}
          </p>
        </div>
      </header>
      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={tab === "profile"} className={tab === "profile" ? "on" : ""} onClick={() => setTab("profile")}>
          Profile
        </button>
        <button role="tab" aria-selected={tab === "connections"} className={tab === "connections" ? "on" : ""} onClick={() => setTab("connections")}>
          Connections
        </button>
      </div>
      {tab === "profile" ? <ProfileTab /> : <ConnectionsTab />}
    </main>
  );
}

function PictureButton() {
  const me = useStore((s) => s.boot!.me);
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="agent-picture">
      <button type="button" className="picture-btn" onClick={() => input.current?.click()} aria-label="Change your picture">
        <Avatar person={{ ...me, kind: "human" }} size={84} />
        <span className="picture-hint">Change picture</span>
      </button>
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file) return;
          setError(null);
          try {
            await api(`/api/avatars/${me.id}`, { dataUrl: await squareImage(file) }, "PUT");
            await loadBootstrap();
          } catch (err) {
            setError(err instanceof Error ? err.message : "Could not upload.");
          }
        }}
      />
      {error && <small className="picture-error">{error}</small>}
    </div>
  );
}

function ProfileTab() {
  const boot = useStore((s) => s.boot)!;
  const me = boot.me as typeof boot.me & { bio?: string; createdAt?: string };
  const [name, setName] = useState(me.name);
  const [editingName, setEditingName] = useState(false);
  const [bio, setBio] = useState(me.bio ?? "");
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [theme, setTheme] = useState<Theme>(readTheme());

  async function save(patch: { name?: string; bio?: string }, what: string) {
    setError(null);
    try {
      await api("/api/me", patch, "PATCH");
      await loadBootstrap();
      setSaved(what);
      setTimeout(() => setSaved(null), 2500);
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save.");
      return false;
    }
  }

  return (
    <>
      <ErrorNote error={error} />
      <section className="card-block">
        <h2>Profile</h2>
        <dl className="profile-facts">
          <dt>Name</dt>
          <dd>
            {editingName ? (
              <form
                className="key-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (await save({ name }, "name")) setEditingName(false);
                }}
              >
                <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required aria-label="Your name" />
                <button className="btn primary small" type="submit">
                  Save
                </button>
                <button className="btn ghost small" type="button" onClick={() => (setName(me.name), setEditingName(false))}>
                  Cancel
                </button>
              </form>
            ) : (
              <>
                {me.name}{" "}
                <button className="linkish" type="button" onClick={() => setEditingName(true)}>
                  Edit
                </button>
                {saved === "name" && <span className="chip ok">saved</span>}
              </>
            )}
          </dd>
          <dt>
            <IconMail /> Email
          </dt>
          <dd>{me.email}</dd>
          <dt>Role</dt>
          <dd>{ROLE_LABEL[boot.workspace.role]}</dd>
          <dt>Sign in with</dt>
          <dd>{[me.hasPassword && "Email and password", me.google && "Google"].filter(Boolean).join(", ")}</dd>
          {me.createdAt && (
            <>
              <dt>Joined</dt>
              <dd>{new Date(me.createdAt).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}</dd>
            </>
          )}
        </dl>
      </section>

      <Fold id="profile-bio" title="Bio" side={saved === "bio" && <span className="chip ok">saved</span>} summary={bio.trim() ? `${bio.trim().split(/\n/)[0]!.slice(0, 140)}` : "Not written yet."}>
        <p className="muted-text">
          Public teammate facts about you: role, responsibilities, goals, accounts, availability and work context. People in your rooms see it on your card, and agents read it when they answer in a room with you. Do not put instructions for agents here.
        </p>
        <textarea className="brief" rows={14} value={bio} onChange={(e) => setBio(e.target.value)} maxLength={8000} placeholder={BIO_TEMPLATE} aria-label="Your bio" />
        <div className="actions">
          {!bio.trim() && (
            <button className="btn ghost small" type="button" onClick={() => setBio(BIO_TEMPLATE)}>
              Start from the outline
            </button>
          )}
          <button className="btn primary small" type="button" onClick={() => void save({ bio }, "bio")}>
            Save bio
          </button>
        </div>
      </Fold>

      <section className="card-block">
        <h2>Appearance</h2>
        <label className="field inline-field">
          <span>Theme on this device</span>
          <select
            value={theme}
            onChange={(e) => {
              const t = e.target.value as Theme;
              setTheme(t);
              applyTheme(t);
            }}
          >
            <option value="system">Follow the system</option>
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
        </label>
      </section>
    </>
  );
}

interface ConnTool {
  name: string;
  description: string;
  readOnly: boolean;
  enabled: boolean;
}
interface Conn {
  id: string;
  kind: string;
  name: string;
  url: string;
  last4: string | null;
  status: string;
  error: string | null;
  lastUsedAt: string | null;
  tools: ConnTool[];
}

const CORTEX_URL = "https://cortex.solutionsguru.ai/mcp/sse";
const GMAIL_NOTES: Record<string, { ok: boolean; text: string }> = {
  connected: { ok: true, text: "Gmail is connected. Agents can now search and read it when you ask them." },
  cancelled: { ok: false, text: "Gmail was not connected: the Google page was closed or access was not allowed." },
  failed: { ok: false, text: "Gmail could not be connected. Try again; if it keeps failing, tell the admin." },
  unavailable: { ok: false, text: "Gmail is not available on this server yet." },
};

function ConnectionsTab() {
  const [list, setList] = useState<Conn[] | null>(null);
  const [gmailReady, setGmailReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<"cortexos" | "mcp" | null>(null);
  const [gmailNote] = useState(() => GMAIL_NOTES[new URLSearchParams(location.search).get("gmail") ?? ""] ?? null);
  const reload = () =>
    api<{ connections: Conn[]; gmail: boolean }>("/api/connections").then(
      (r) => (setList(r.connections), setGmailReady(r.gmail)),
      (err) => setError(err instanceof ApiError ? err.message : "Could not load your connections."),
    );
  useEffect(() => {
    void reload();
    if (location.search) history.replaceState(null, "", `${location.pathname}#connections`);
  }, []);
  const hasCortex = list?.some((c) => c.kind === "cortexos");

  return (
    <>
      <ErrorNote error={error} />
      <section className="card-block">
        <h2>Connections</h2>
        <p className="muted-text">
          Your own tools for agents. A connection is yours: Claude and the other agents use it only when you ask them something, never for someone else, and only tools that read. What they find goes into their reply, which everyone in the room reads, so ask about personal things in rooms you trust.
        </p>
        {gmailNote && <p className={`notice-line ${gmailNote.ok ? "ok" : ""}`}>{gmailNote.text}</p>}
        {!list && !error && <p className="muted-text">Loading</p>}
        {list && list.length === 0 && <p className="muted-text">Nothing connected yet.</p>}
      </section>
      {list?.map((c) => (
        <ConnectionCard key={c.id} conn={c} onChanged={reload} />
      ))}
      <Fold id="profile-add-connection" title="Add a connection" summary="CortexOS memory, Gmail, or another MCP server.">
        <ul className="rows">
          {!hasCortex && (
            <li>
              <div>
                <b>CortexOS memory</b>
                <small>Lets your agents search your CortexOS memory and life notes when you ask them something. Connects through its MCP server with your key.</small>
              </div>
              <button className="btn primary small" type="button" onClick={() => setAdding("cortexos")}>
                Connect
              </button>
            </li>
          )}
          <li>
            <div>
              <b>Gmail</b>
              <small>Lets your agents search and read the Gmail account you choose, when you ask. Google sign in, read only: agents cannot send, delete or change anything. You can link more than one account.</small>
            </div>
            {gmailReady ? (
              <a className="btn primary small" href="/api/connections/gmail/start">
                Connect
              </a>
            ) : (
              <span className="chip">not available</span>
            )}
          </li>
          <li>
            <div>
              <b>Another MCP server</b>
              <small>Any MCP server on a public https address, with its key if it needs one. You choose which of its reading tools agents may use.</small>
            </div>
            <button className="btn ghost small" type="button" onClick={() => setAdding("mcp")}>
              Add
            </button>
          </li>
        </ul>
      </Fold>
      {adding && <AddConnection preset={adding} onClose={() => setAdding(null)} onSaved={() => (setAdding(null), void reload())} />}
    </>
  );
}

function ConnectionCard({ conn: c, onChanged }: { conn: Conn; onChanged: () => void }) {
  const [on, setOn] = useState(() => new Set(c.tools.filter((t) => t.enabled).map((t) => t.name)));
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [showActs, setShowActs] = useState(false);
  useEffect(() => setOn(new Set(c.tools.filter((t) => t.enabled).map((t) => t.name))), [c]);
  const reads = c.tools.filter((t) => t.readOnly);
  const acts = c.tools.filter((t) => !t.readOnly);
  const dirty = reads.some((t) => t.enabled !== on.has(t.name));

  async function run(what: () => Promise<unknown>, done: string) {
    setBusy(true);
    setNote(null);
    try {
      await what();
      setNote(done);
      onChanged();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : "That did not work.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Fold
      id={`connection-${c.id}`}
      title={c.name}
      side={<span className={`chip ${c.status === "ok" ? "ok" : ""}`}>{c.status === "ok" ? "connected" : c.status}</span>}
      summary={`${on.size} of ${reads.length} reading tools on${on.size ? `: ${[...on].join(", ")}` : ""}.`}
    >
      <p className="muted-text">
        {c.url}
        {c.last4 && ` · key ending ${c.last4}`}
        {c.lastUsedAt && ` · last used ${new Date(c.lastUsedAt).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`}
      </p>
      {c.error && <p className="muted-text">Last error: {c.error}</p>}
      <h3 className="sub-head">
        Tools agents may use ({on.size} of {reads.length} that read)
      </h3>
      <ul className="tool-list">
        {reads.map((t) => (
          <li key={t.name}>
            <label>
              <input
                type="checkbox"
                checked={on.has(t.name)}
                onChange={(e) => {
                  const next = new Set(on);
                  if (e.target.checked) next.add(t.name);
                  else next.delete(t.name);
                  setOn(next);
                }}
              />
              <span>
                <b>{t.name}</b>
                <small>{t.description.slice(0, 220)}</small>
              </span>
            </label>
          </li>
        ))}
      </ul>
      {acts.length > 0 && (
        <p className="muted-text">
          {acts.length} more {acts.length === 1 ? "tool acts" : "tools act"} (send, store, create, delete) and stay off: agents in Agora only read.{" "}
          <button className="linkish" type="button" onClick={() => setShowActs(!showActs)}>
            {showActs ? "Hide them" : "Show them"}
          </button>
        </p>
      )}
      {showActs && <p className="muted-text">{acts.map((t) => t.name).join(", ")}</p>}
      <div className="actions">
        {note && <small className="muted-text">{note}</small>}
        <button
          className="btn ghost small"
          type="button"
          disabled={busy}
          onClick={() => {
            if (confirm(`Disconnect ${c.name}? Agora deletes its key; agents stop using it.`)) void run(() => api(`/api/connections/${c.id}`, undefined, "DELETE"), "Disconnected.");
          }}
        >
          Disconnect
        </button>
        <button className="btn ghost small" type="button" disabled={busy} onClick={() => void run(() => api(`/api/connections/${c.id}/refresh`, {}), "Tools listed again.")}>
          Check again
        </button>
        <button className="btn primary small" type="button" disabled={busy || !dirty} onClick={() => void run(() => api(`/api/connections/${c.id}`, { enabledTools: [...on] }, "PATCH"), "Saved.")}>
          Save tools
        </button>
      </div>
    </Fold>
  );
}

function AddConnection({ preset, onClose, onSaved }: { preset: "cortexos" | "mcp"; onClose: () => void; onSaved: () => void }) {
  const cortex = preset === "cortexos";
  const [name, setName] = useState(cortex ? "CortexOS memory" : "");
  const [url, setUrl] = useState(cortex ? CORTEX_URL : "");
  const [token, setToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={cortex ? "Connect CortexOS memory" : "Add an MCP server"} onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await api("/api/connections", { preset, name, url, token: token || undefined });
            onSaved();
          } catch (err) {
            setError(err instanceof ApiError ? err.message : "Could not connect.");
            setBusy(false);
          }
        }}
      >
        <ErrorNote error={error} />
        {!cortex && (
          <label className="field">
            <span>Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} required placeholder="For example Notion" />
          </label>
        )}
        <label className="field">
          <span>MCP address (https)</span>
          <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} required />
        </label>
        <label className="field">
          <span>Key{cortex ? " (MCP_SERVER_API_KEY of CortexOS)" : " (if the server needs one)"}</span>
          <input type="password" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" required={cortex} />
          <small>Stored encrypted. Agora shows only its last 4 characters.</small>
        </label>
        <div className="actions">
          <button className="btn ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="submit" disabled={busy}>
            {busy ? "Connecting" : "Connect"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
